import React, { useState, useEffect, useRef } from 'react';
import { Brain, Upload, FileText, AlertCircle, CheckCircle, Loader2, User, Shield, Briefcase, XCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { supabase } from '../../lib/supabaseClient';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Above this many files, processing switches from the inline (browser-tab-must-stay-open)
// path to the durable queue, since a long-lived request doing sequential OpenAI calls
// in the browser can't reliably survive a real backlog.
const LARGE_BATCH_THRESHOLD = 5;
const BATCH_POLL_INTERVAL_MS = 3000;

const AICVIngestion = () => {
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Large-batch (queued) mode state
  const [queuedBatchId, setQueuedBatchId] = useState(null);
  const [queuedBatchStatus, setQueuedBatchStatus] = useState(null);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  const pollBatchStatus = (batchId) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);

    const poll = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch(`${process.env.REACT_APP_API_URL}/cv-ingestion/batch/${batchId}/status`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        const data = await response.json();

        if (!response.ok) return;

        setQueuedBatchStatus(data);

        if (data.complete && pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setProcessing(false);
        }
      } catch (err) {
        console.error('Error polling batch status:', err);
      }
    };

    poll();
    pollTimerRef.current = setInterval(poll, BATCH_POLL_INTERVAL_MS);
  };

  const handleEnqueueBatch = async () => {
    setProcessing(true);
    setError(null);
    setResults(null);
    setQueuedBatchStatus(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required');
      }

      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/cv-ingestion/enqueue-batch`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to enqueue batch');
      }

      setQueuedBatchId(data.batchId);
      pollBatchStatus(data.batchId);

    } catch (err) {
      console.error('Error enqueueing batch:', err);
      setError(err.message);
      setProcessing(false);
    }
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);

    // Filter for valid file types
    const validFiles = selectedFiles.filter(file => {
      const extension = file.name.toLowerCase().split('.').pop();
      return ['pdf', 'doc', 'docx', 'txt'].includes(extension);
    });

    if (validFiles.length !== selectedFiles.length) {
      alert('Some files were excluded. Only PDF, DOC, DOCX, and TXT files are supported.');
    }

    setFiles(validFiles);
    setResults(null);
    setError(null);
    setQueuedBatchId(null);
    setQueuedBatchStatus(null);
  };

  const extractTextFromFile = async (file) => {
    const extension = file.name.toLowerCase().split('.').pop();

    try {
      if (extension === 'txt') {
        // For text files, just read directly
        return await file.text();
      }

      if (extension === 'pdf') {
        // Extract text from PDF using PDF.js
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';

        // Extract text from each page
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          fullText += pageText + '\n';
        }

        return fullText.trim();
      }

      if (extension === 'doc' || extension === 'docx') {
        // Extract text from DOCX using Mammoth
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        return result.value;
      }

      throw new Error(`Unsupported file type: ${extension}`);
    } catch (error) {
      console.error(`Error extracting text from ${file.name}:`, error);
      throw new Error(`Failed to extract text from ${extension.toUpperCase()} file: ${error.message}`);
    }
  };

  const handleProcess = async () => {
    if (files.length === 0) {
      alert('Please select at least one resume file');
      return;
    }

    // Large batches go through the durable queue instead of one long-lived
    // request with sequential OpenAI calls in this browser tab.
    if (files.length > LARGE_BATCH_THRESHOLD) {
      return handleEnqueueBatch();
    }

    setProcessing(true);
    setResults(null);
    setError(null);

    try {
      // Get auth token (also needed to upload each file to Storage below)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required');
      }

      // Extract text from all files, and separately keep each file's own
      // durable copy in Storage - this pipeline only ever sent extracted
      // text to the backend, so ingested candidates had no downloadable CV
      // at all. A failed upload here doesn't block ingestion; the candidate
      // just ends up without a stored file, same as before this fix.
      const cvBatch = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const cvText = await extractTextFromFile(file);

          let cvFile = null;
          try {
            const storagePath = `bulk-ingestion/${Date.now()}_${file.name}`;
            const { error: uploadError } = await supabase.storage
              .from('candidate-documents')
              .upload(storagePath, file);

            if (uploadError) throw uploadError;

            const { data: urlData } = supabase.storage
              .from('candidate-documents')
              .getPublicUrl(storagePath);

            cvFile = { url: urlData.publicUrl, filename: file.name, size: file.size };
          } catch (uploadError) {
            console.error(`Error uploading ${file.name} to storage:`, uploadError);
          }

          cvBatch.push({
            cvText,
            metadata: {
              filename: file.name,
              fileSize: file.size,
              fileType: file.type
            },
            options: cvFile ? { cvFile } : {}
          });
        } catch (extractError) {
          console.error(`Error extracting text from ${file.name}:`, extractError);
          cvBatch.push({
            cvText: null,
            metadata: {
              filename: file.name,
              error: extractError.message
            }
          });
        }
      }

      // Filter out failed extractions
      const validCVs = cvBatch.filter(cv => cv.cvText !== null);
      const failedExtractions = cvBatch.filter(cv => cv.cvText === null);

      // Check if all extractions failed
      if (validCVs.length === 0) {
        throw new Error(
          `Failed to extract text from all ${files.length} file(s). ` +
          `Please ensure your PDFs are not password-protected or corrupted. ` +
          `Try using .txt or .docx files instead.`
        );
      }

      // Call CV ingestion API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cv-ingestion/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          cvBatch: validCVs,
          options: {
            source: 'bulk_upload',
            continueOnError: true,
            upsertBy: 'email'
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to process CVs');
      }

      // Merge extraction errors with API results
      const failedResults = failedExtractions.map(cv => ({
        filename: cv.metadata.filename,
        success: false,
        error: `Text extraction failed: ${cv.metadata.error}`
      }));

      const successfulResults = (data.results?.items || data.results || []).map(item => ({
        filename: item.metadata?.filename || item.filename,
        ...item.result
      }));

      const allResults = [...failedResults, ...successfulResults];

      setResults({
        ...(data.results || {}),
        items: allResults,
        summary: {
          ...(data.results?.summary || data.summary || {}),
          total: allResults.length,
          extractionFailed: failedExtractions.length
        }
      });

    } catch (err) {
      console.error('Error processing CVs:', err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const clearFiles = () => {
    setFiles([]);
    setResults(null);
    setError(null);
    setQueuedBatchId(null);
    setQueuedBatchStatus(null);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    const fileInput = document.getElementById('cv-file-input');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            <CardTitle>AI Resume Ingestion</CardTitle>
          </div>
          <CardDescription>
            Upload resume files and let AI automatically extract candidate information, skills, and clearance levels
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File Upload */}
          <div>
            <label className="text-sm font-medium mb-2 block">Select Resume Files</label>
            <div className="space-y-3">
              <input
                id="cv-file-input"
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                multiple
                onChange={handleFileSelect}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-medium
                  file:bg-primary file:text-primary-foreground
                  hover:file:bg-primary/90
                  cursor-pointer"
              />
              <p className="text-xs text-muted-foreground">
                Supported formats: PDF, DOCX, TXT
              </p>
            </div>
          </div>

          {/* Selected Files */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Selected Files ({files.length})</label>
                <Button variant="ghost" size="sm" onClick={clearFiles}>
                  Clear All
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {files.map((file, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    {file.name}
                    <span className="text-xs text-muted-foreground ml-1">
                      ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                  </Badge>
                ))}
              </div>
              {files.length > LARGE_BATCH_THRESHOLD && (
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    {files.length} files will be queued and processed in the background -
                    you can navigate away and check back on progress here.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Process Button */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleProcess}
              disabled={files.length === 0 || processing}
              className="w-full sm:w-auto"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {files.length > LARGE_BATCH_THRESHOLD
                    ? 'Queueing...'
                    : `Processing ${files.length} resume${files.length !== 1 ? 's' : ''}...`}
                </>
              ) : (
                <>
                  <Brain className="h-4 w-4 mr-2" />
                  {files.length > LARGE_BATCH_THRESHOLD ? 'Queue for Processing' : 'Process with AI'}
                </>
              )}
            </Button>
            {files.length > 0 && !processing && (
              <span className="text-sm text-muted-foreground">
                Ready to process {files.length} file{files.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Info Alert */}
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1 text-sm">
                <p className="font-medium">AI will extract:</p>
                <ul className="list-disc list-inside space-y-0.5 text-muted-foreground ml-2">
                  <li>Personal information (name, email, phone, location)</li>
                  <li>Security clearance levels (Baseline, NV1, NV2, TSPV)</li>
                  <li>Technical and behavioral skills</li>
                  <li>Employment history and education</li>
                  <li>Citizenship (inferred from clearance)</li>
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium">Error processing resumes</p>
            <p className="text-sm mt-1">{error}</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Queued Batch Progress */}
      {queuedBatchId && queuedBatchStatus && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {queuedBatchStatus.complete ? 'Batch Complete' : 'Processing in Background'}
                </CardTitle>
                <CardDescription>
                  {(queuedBatchStatus.counts.done || 0)} done, {(queuedBatchStatus.counts.failed || 0)} failed,{' '}
                  {(queuedBatchStatus.counts.queued || 0) + (queuedBatchStatus.counts.processing || 0)} remaining
                  {' '}out of {queuedBatchStatus.total}
                </CardDescription>
              </div>
              {!queuedBatchStatus.complete && (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queuedBatchStatus.jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-mono text-xs">{job.filename}</TableCell>
                    <TableCell>
                      {job.status === 'done' && <Badge variant="default" className="text-xs">Done</Badge>}
                      {job.status === 'failed' && <Badge variant="destructive" className="text-xs">Failed</Badge>}
                      {(job.status === 'queued' || job.status === 'processing') && (
                        <Badge variant="outline" className="text-xs">
                          {job.status === 'processing' ? 'Processing...' : 'Queued'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.status === 'done' && job.result?.candidate && (
                        <span>
                          {job.result.candidate.first_name} {job.result.candidate.last_name}
                          {job.result.candidate.isNew ? ' (new)' : ' (updated)'}
                        </span>
                      )}
                      {job.status === 'failed' && (
                        <span className="text-destructive">{job.error_message}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Results Display */}
      {results && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Processing Results</CardTitle>
                <CardDescription>
                  {results.successful || 0} successful, {results.failed || 0} failed out of {results.total || 0} resumes
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Badge variant="default" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {results.successful || 0} Success
                </Badge>
                {results.failed > 0 && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    {results.failed} Failed
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {results.items?.map((item, index) => (
                <Card key={index} className={item.success ? 'border-green-200' : 'border-red-200'}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {item.success ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <CardTitle className="text-sm">
                          {item.filename || `Resume ${index + 1}`}
                        </CardTitle>
                      </div>
                      {item.success && (
                        <Badge variant={item.candidate?.isNew ? 'default' : 'secondary'}>
                          {item.candidate?.isNew ? 'New Candidate' : 'Updated Candidate'}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {item.success ? (
                      <>
                        {/* Candidate Info */}
                        {item.candidate && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">
                                {item.candidate.first_name} {item.candidate.last_name}
                              </span>
                              {item.candidate.email && (
                                <span className="text-sm text-muted-foreground">
                                  ({item.candidate.email})
                                </span>
                              )}
                            </div>

                            {/* Clearance & Citizenship */}
                            <div className="flex flex-wrap gap-2">
                              {item.clearance?.detected && (
                                <Badge variant="outline" className="flex items-center gap-1">
                                  <Shield className="h-3 w-3" />
                                  Clearance: {item.clearance.level}
                                </Badge>
                              )}
                              {item.citizenship?.inferred && (
                                <Badge variant="outline">
                                  {item.citizenship.status}
                                </Badge>
                              )}
                            </div>

                            {/* Skills */}
                            {item.skills && item.skills.total > 0 && (
                              <div className="flex items-center gap-2">
                                <Briefcase className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  {item.skills.total} skill{item.skills.total !== 1 ? 's' : ''} mapped
                                  {item.skills.created > 0 && (
                                    <span className="text-muted-foreground">
                                      {' '}({item.skills.created} new)
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}

                            {/* Processing Time */}
                            {item.duration && (
                              <p className="text-xs text-muted-foreground">
                                Processed in {(item.duration / 1000).toFixed(2)}s
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <p className="text-sm">{item.error || 'Unknown error occurred'}</p>
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats Table */}
      {results && results.items && results.items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Clearance</TableHead>
                  <TableHead>Skills</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-mono text-xs">
                      {item.filename || `Resume ${index + 1}`}
                    </TableCell>
                    <TableCell>
                      {item.success ? (
                        <Badge variant="default" className="text-xs">Success</Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs">Failed</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.candidate ? (
                        <div className="space-y-0.5">
                          <div>{item.candidate.first_name} {item.candidate.last_name}</div>
                          {item.candidate.email && (
                            <div className="text-xs text-muted-foreground">{item.candidate.email}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.clearance?.detected ? (
                        <Badge variant="outline" className="text-xs">
                          {item.clearance.level}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.skills?.total || 0}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {item.duration ? `${(item.duration / 1000).toFixed(1)}s` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AICVIngestion;
